import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkles } from "lucide-react";

const BAND_STYLE = {
  strong: "border-emerald-200 bg-emerald-50 text-emerald-700",
  good: "border-sky-200 bg-sky-50 text-sky-700",
  potential: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-zinc-200 bg-zinc-50 text-zinc-500",
};

const TOOLTIP = "Your match is based on your profile, location, content and the requirements set by the business. It does not guarantee acceptance.";

export function MatchBadge({ match, size = "sm", showLabel = true, testid = "match-badge" }) {
  if (!match || typeof match.score !== "number") return null;
  const style = BAND_STYLE[match.band] || BAND_STYLE.low;
  const pad = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-xs";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid={testid} className={`inline-flex cursor-help items-center gap-1.5 rounded-full border font-semibold ${style} ${pad}`}>
            <Sparkles className={size === "lg" ? "h-4 w-4" : "h-3 w-3"} />
            <span>{match.score}% Match</span>
            {showLabel && <span className="opacity-70">· {match.label}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function MatchReasons({ match, testid = "match-reasons" }) {
  if (!match?.reasons?.length && !match?.improve?.length) return null;
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-100 bg-white p-4">
      <h3 className="font-display font-bold text-zinc-900">Why you're a {match.label.toLowerCase()}</h3>
      <ul className="mt-3 space-y-1.5">
        {match.reasons.map((r, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className={r.matched ? "text-emerald-600" : "text-zinc-400"}>{r.matched ? "✓" : "○"}</span>
            <span className={r.matched ? "text-zinc-700" : "text-zinc-500"}>{r.text}</span>
          </li>
        ))}
      </ul>
      {match.improve?.length > 0 && (
        <div className="mt-4 rounded-xl bg-emerald-50/70 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Improve your matches</p>
          <ul className="mt-1.5 space-y-1">
            {match.improve.map((h, i) => <li key={i} className="text-sm text-emerald-900">{h}</li>)}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[11px] text-zinc-400">Match scores are a recommendation to help you find relevant collaborations — they don't guarantee acceptance.</p>
    </div>
  );
}
